import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import AiInHealthcareOperations from './AiInHealthcareOperations'
import DownloadForm from '../../components/common/DownloadForm'
import { WHITE_PAPERS, getWhitePaperById } from '@/data/whitePapers'

const sections = [
  { id: "exec-summary", labelKey: "whitePaperDetail.sections.executiveSummary" },
  { id: "challenge", labelKey: "whitePaperDetail.sections.challenge" },
  { id: "approach", labelKey: "whitePaperDetail.sections.approach" },
  { id: "architecture", labelKey: "whitePaperDetail.sections.architecture" },
  { id: "outcomes", labelKey: "whitePaperDetail.sections.outcomes" },
  { id: "security", labelKey: "whitePaperDetail.sections.security" },
  { id: "case-study", labelKey: "whitePaperDetail.sections.caseStudy" },
  { id: "next-steps", labelKey: "whitePaperDetail.sections.nextSteps" },
]

const WhitePaperDetail: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>(sections[0].id)

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const paper = useMemo(() => {
    return getWhitePaperById(id || '') || WHITE_PAPERS[0]
  }, [id])

  // Special case for AI Healthcare Operations white paper
  if (id === 'wp-009') {
    return <AiInHealthcareOperations />
  }

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className={`pt-20 pb-10 border-b border-slate-200 ${paper.heroGradient}`}>
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            {t('whitePaperDetail.hero.badge')}
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            {paper.title}
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            {paper.subtitle}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label={t('whitePaperDetail.hero.meta.author')} value={paper.author} />
            <MetaItem label={t('whitePaperDetail.hero.meta.published')} value={paper.displayDate} />
            <MetaItem label={t('whitePaperDetail.hero.meta.length')} value={paper.readTime} />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {t('whitePaperDetail.hero.actions.downloadPdf')}
            </button>
            <button
              onClick={() => navigator.share?.({ title: paper.title, url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              {t('whitePaperDetail.hero.actions.share')}
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {t(s.labelKey)}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.execSummary.paragraph')}
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">{t('whitePaperDetail.content.execSummary.atAGlance')}</span> {t('whitePaperDetail.content.execSummary.atAGlanceText')}</p>
                    </div>
                  </>
                )}
                {s.id === "challenge" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.challenge.paragraph')}
                    </p>
                    <ul>
                      {(t('whitePaperDetail.content.challenge.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </>
                )}
                {s.id === "approach" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.approach.paragraph')}
                    </p>
                    <ol>
                      {(t('whitePaperDetail.content.approach.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ol>
                  </>
                )}
                {s.id === "architecture" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.architecture.paragraph')}
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 p-4">
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t('whitePaperDetail.content.architecture.diagramPlaceholder')}</div>
                      <div className="aspect-[16/9] w-full rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center border border-dashed border-slate-300">
                        <span className="text-slate-400">{t('whitePaperDetail.content.architecture.diagramText')}</span>
                      </div>
                    </div>
                  </>
                )}
                {s.id === "outcomes" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.outcomes.paragraph')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                      {[
                        { kpi: t('whitePaperDetail.content.outcomes.kpis.waitTime'), blurb: t('whitePaperDetail.content.outcomes.kpis.waitTimeLabel') },
                        { kpi: t('whitePaperDetail.content.outcomes.kpis.throughput'), blurb: t('whitePaperDetail.content.outcomes.kpis.throughputLabel') },
                        { kpi: t('whitePaperDetail.content.outcomes.kpis.rework'), blurb: t('whitePaperDetail.content.outcomes.kpis.reworkLabel') },
                      ].map((x) => (
                        <div key={x.blurb} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="text-3xl font-bold text-slate-900">{x.kpi}</div>
                          <div className="text-sm text-slate-600">{x.blurb}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {s.id === "security" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.security.paragraph')}
                    </p>
                  </>
                )}
                {s.id === "case-study" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.caseStudy.paragraph')}
                    </p>
                    <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">{t('whitePaperDetail.content.caseStudy.result')}</span> {t('whitePaperDetail.content.caseStudy.resultText')}</p>
                    </div>
                  </>
                )}
                {s.id === "next-steps" && (
                  <>
                    <p>
                      {t('whitePaperDetail.content.nextSteps.paragraph')}
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      {t('whitePaperDetail.content.nextSteps.downloadFullPaper')}
                    </button>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">{t('whitePaperDetail.sidebar.summary.title')}</div>
              <p className="mt-2 text-sm text-slate-700">
                {t('whitePaperDetail.sidebar.summary.description')}
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                {(t('whitePaperDetail.sidebar.summary.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {t('whitePaperDetail.sidebar.summary.downloadPdf')}
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">{t('whitePaperDetail.sidebar.toc.title')}</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {t(s.labelKey)}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">{t('whitePaperDetail.sidebar.citation.title')}</div>
              <p className="mt-2 text-xs text-slate-700">
                {t('whitePaperDetail.sidebar.citation.format', { title: paper.title, date: paper.displayDate })}
              </p>
              <button
                onClick={() => {
                  const text = t('whitePaperDetail.sidebar.citation.format', { title: paper.title, date: paper.displayDate })
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                {t('whitePaperDetail.sidebar.citation.copyCitation')}
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">{t('whitePaperDetail.relatedPapers.title')}</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(t('whitePaperDetail.relatedPapers.papers', { returnObjects: true }) as Array<{title: string, description: string}>).map((card, index: number) => (
              <a
                key={index}
                href="#"
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </a>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              {t('whitePaperDetail.relatedPapers.backToFinder')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle={paper.title}
        pdfPath={paper.pdfUrl}
      />
    </div>
  )
}

export default WhitePaperDetail
